import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { 
  Shield, CheckCircle, Clock, AlertCircle, ExternalLink, Upload, FileText, Loader2, X
} from "lucide-react";

const VerificationPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [provinces, setProvinces] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    registration_number: "",
    province: user?.province || "",
    document_url: "",
    practice_permit_url: ""
  });
  const [idFile, setIdFile] = useState(null);
  const [permitFile, setPermitFile] = useState(null);
  const [uploading, setUploading] = useState({ id: false, permit: false });
  const idInputRef = useRef(null);
  const permitInputRef = useRef(null);

  const REGISTRY_LINKS = {
    AB: { name: "College of Registered Dental Hygienists of Alberta", url: "https://crdha.ca" },
    BC: { name: "College of Dental Hygienists of BC", url: "https://cdhbc.com" },
    MB: { name: "College of Dental Hygienists of Manitoba", url: "https://cdhm.info" },
    NB: { name: "New Brunswick Dental Hygienists Association", url: "https://nbdha.ca" },
    NL: { name: "College of Dental Hygienists of NL", url: "https://crdhnl.ca" },
    NS: { name: "College of Dental Hygienists of Nova Scotia", url: "https://cdhns.ca" },
    ON: { name: "College of Dental Hygienists of Ontario", url: "https://cdho.org" },
    PE: { name: "Dental Hygienists Association of PEI", url: "https://dhapei.ca" },
    QC: { name: "Ordre des hygiénistes dentaires du Québec", url: "https://ohdq.com" },
    SK: { name: "Saskatchewan Dental Hygienists Association", url: "https://sdha.ca" },
    NT: { name: "Northwest Territories", url: "https://www.gov.nt.ca" },
    NU: { name: "Nunavut", url: "https://www.gov.nu.ca" },
    YT: { name: "Yukon", url: "https://www.gov.yk.ca" }
  };

  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await axios.get(`${API}/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error("Failed to fetch provinces");
    }
  };

  const MAX_BYTES = 10 * 1024 * 1024;
  const ACCEPT_MIME = ["image/jpeg", "image/png", "application/pdf"];
  const ACCEPT_EXT = /\.(pdf|jpe?g|png)$/i;

  const validateFile = (file) => {
    if (!file) return "No file selected";
    if (file.size > MAX_BYTES) return "File is larger than 10 MB";
    if (!ACCEPT_MIME.includes(file.type) || !ACCEPT_EXT.test(file.name || "")) {
      return "Only PDF, JPG or PNG files are accepted";
    }
    return null;
  };

  const uploadDoc = async (file, docType) => {
    const err = validateFile(file);
    if (err) { toast.error(err); return null; }
    setUploading((u) => ({ ...u, [docType]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const res = await axios.post(`${API}/users/upload-verification-doc`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.file_id;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      return null;
    } finally {
      setUploading((u) => ({ ...u, [docType]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.registration_number.trim() || !formData.province) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!idFile || !permitFile) {
      toast.error("Please upload both your Government Photo ID and your Active Practice Permit");
      return;
    }

    setSubmitting(true);
    try {
      const idUrl = await uploadDoc(idFile, "id");
      if (!idUrl) { setSubmitting(false); return; }
      const permitUrl = await uploadDoc(permitFile, "permit");
      if (!permitUrl) { setSubmitting(false); return; }

      await axios.post(`${API}/users/submit-verification`, {
        ...formData,
        document_file_id: idUrl,
        practice_permit_file_id: permitUrl,
      });
      await refreshUser();
      toast.success("Verification submitted! We'll review your credentials shortly.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit verification");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusContent = () => {
    const status = user?.verification_status;

    if (status === "verified") {
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#10B981]/10 border border-[#10B981] rounded-2xl p-8 text-center"
        >
          <div className="w-20 h-20 rounded-full bg-[#10B981] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            You're Verified!
          </h2>
          <p className="text-[#64748B] mb-4">
            Your credentials have been verified. You have full access to all community features.
          </p>
          {user?.registration_number && (
            <p className="text-sm text-[#6B4E8A]">
              Registration: <span className="font-medium">{user.registration_number}</span>
              {user?.province && ` (${user.province})`}
            </p>
          )}
        </motion.div>
      );
    }

    if (status === "submitted") {
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#8DB5A0]/10 border border-[#8DB5A0] rounded-2xl p-8 text-center"
        >
          <div className="w-20 h-20 rounded-full bg-[#8DB5A0] flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Under Review
          </h2>
          <p className="text-[#64748B] mb-4">
            Your verification is being reviewed. This usually takes 1-2 business days.
          </p>
          <p className="text-sm text-[#6B4E8A]">
            Registration: <span className="font-medium">{user?.registration_number}</span>
            {user?.province && ` (${user.province})`}
          </p>
        </motion.div>
      );
    }

    if (status === "rejected") {
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#EF4444]/10 border border-[#EF4444] rounded-2xl p-8 text-center mb-8"
        >
          <div className="w-20 h-20 rounded-full bg-[#EF4444] flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Verification Rejected
          </h2>
          <p className="text-[#64748B] mb-4">
            Unfortunately, we couldn't verify your credentials. Please submit again with correct information.
          </p>
        </motion.div>
      );
    }

    return null;
  };

  const showForm = user?.verification_status === "pending" || user?.verification_status === "rejected";

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-2xl" data-testid="verification-page">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <EditableText
            blockKey="verification.title"
            fallback="Verification"
            as="h1"
            className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          />
          <EditableText
            blockKey="verification.subtitle"
            fallback="Verify your dental hygienist credentials to access all community features"
            as="p"
            className="text-[#64748B]"
            multiline
          />
        </motion.div>

        {/* Status Content */}
        {getStatusContent()}

        {/* Verification Form */}
        {showForm && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-[#E5DFEC] rounded-2xl p-6 md:p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#8DB5A0] flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#6B4E8A]" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Submit Your Credentials
                </h2>
                <p className="text-sm text-[#64748B]">
                  We'll verify your registration with the provincial registry
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="province">Province/Territory</Label>
                <Select
                  value={formData.province}
                  onValueChange={(value) => setFormData({ ...formData, province: value })}
                >
                  <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]" data-testid="verification-province-select">
                    <SelectValue placeholder="Select your province" />
                  </SelectTrigger>
                  <SelectContent>
                    {provinces.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.province && REGISTRY_LINKS[formData.province] && (
                  <a
                    href={REGISTRY_LINKS[formData.province].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#8DB5A0] hover:text-[#D0694E] mt-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {REGISTRY_LINKS[formData.province].name}
                  </a>
                )}
              </div>

              <div>
                <Label htmlFor="registration_number">Registration Number</Label>
                <Input
                  id="registration_number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  placeholder="Enter your registration number"
                  className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                  data-testid="registration-number-input"
                />
                <p className="text-xs text-[#64748B] mt-1">
                  This is your unique identifier with your provincial regulatory body
                </p>
              </div>

              <div>
                <Label>Government Photo ID *</Label>
                <input
                  ref={idInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={(e) => { const f = e.target.files?.[0] || null; const err = f && validateFile(f); if (err) { toast.error(err); e.target.value = ""; setIdFile(null); } else setIdFile(f); }}
                  className="hidden"
                  data-testid="id-file-input"
                />
                {idFile ? (
                  <div className="mt-2 flex items-center justify-between gap-2 px-4 py-3 bg-white border border-[#E5DFEC] rounded-xl" data-testid="id-file-pill">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-[#8DB5A0] shrink-0" />
                      <span className="text-sm text-[#2A2A2A] truncate">{idFile.name}</span>
                      <span className="text-xs text-[#64748B] shrink-0">({(idFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setIdFile(null); if (idInputRef.current) idInputRef.current.value = ""; }}
                      className="text-[#64748B] hover:text-[#EF4444] shrink-0"
                      data-testid="id-file-clear"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => idInputRef.current?.click()}
                    className="mt-2 w-full flex flex-col items-center justify-center gap-1 px-4 py-6 bg-white border-2 border-dashed border-[#E5DFEC] hover:border-[#6B4E8A] hover:bg-[#F0ECF7]/40 rounded-xl transition-colors"
                    data-testid="id-file-trigger"
                  >
                    <Upload className="w-5 h-5 text-[#6B4E8A]" />
                    <span className="text-sm font-medium text-[#6B4E8A]">Upload Photo ID</span>
                    <span className="text-xs text-[#64748B]">PDF, JPG or PNG · Max 10 MB · Private, founder-only, deleted after review</span>
                  </button>
                )}
                <p className="text-xs text-[#64748B] mt-1">
                  A photo of your driver's licence, passport, or other government-issued photo ID.
                </p>
              </div>

              <div>
                <Label>Active Practice Permit *</Label>
                <input
                  ref={permitInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={(e) => { const f = e.target.files?.[0] || null; const err = f && validateFile(f); if (err) { toast.error(err); e.target.value = ""; setPermitFile(null); } else setPermitFile(f); }}
                  className="hidden"
                  data-testid="permit-file-input"
                />
                {permitFile ? (
                  <div className="mt-2 flex items-center justify-between gap-2 px-4 py-3 bg-white border border-[#E5DFEC] rounded-xl" data-testid="permit-file-pill">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-[#8DB5A0] shrink-0" />
                      <span className="text-sm text-[#2A2A2A] truncate">{permitFile.name}</span>
                      <span className="text-xs text-[#64748B] shrink-0">({(permitFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPermitFile(null); if (permitInputRef.current) permitInputRef.current.value = ""; }}
                      className="text-[#64748B] hover:text-[#EF4444] shrink-0"
                      data-testid="permit-file-clear"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => permitInputRef.current?.click()}
                    className="mt-2 w-full flex flex-col items-center justify-center gap-1 px-4 py-6 bg-white border-2 border-dashed border-[#E5DFEC] hover:border-[#6B4E8A] hover:bg-[#F0ECF7]/40 rounded-xl transition-colors"
                    data-testid="permit-file-trigger"
                  >
                    <Upload className="w-5 h-5 text-[#6B4E8A]" />
                    <span className="text-sm font-medium text-[#6B4E8A]">Upload Practice Permit</span>
                    <span className="text-xs text-[#64748B]">PDF, JPG or PNG · Max 10 MB · Private, founder-only, deleted after review</span>
                  </button>
                )}
                <p className="text-xs text-[#64748B] mt-1">
                  The current, active practice permit issued by your provincial regulator.
                </p>
              </div>

              <div className="bg-[#FAF7F2] rounded-xl p-4">
                <h4 className="font-medium text-[#6B4E8A] mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Verification Process
                </h4>
                <ol className="text-sm text-[#64748B] space-y-1 list-decimal list-inside">
                  <li>Submit your registration details</li>
                  <li>Our team verifies with the provincial registry</li>
                  <li>Get verified status within 1-2 business days</li>
                  <li>Enjoy full access to the community</li>
                </ol>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full py-6"
                disabled={submitting || uploading.id || uploading.permit}
                data-testid="submit-verification-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading & submitting…</>
                ) : "Submit for Verification"}
              </Button>
            </form>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default VerificationPage;
